import { useCallback, useEffect, useState } from 'react';
import type { SupplierProfile } from '../../lib/api';
import { riskAPI, type RiskAssessment, type RiskCategory, type RiskLevel } from '../../lib/api/risk';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import RiskMatrixModal from '../../components/suppliers/RiskMatrixModal';
import { AlertCircle, Grid3X3, RefreshCw, ShieldAlert } from 'lucide-react';
import { formatDay } from '../barratt/uploadCycle';
import RiskAssessmentDialog from './RiskAssessmentDialog';

interface RiskTabProps {
  supplier: SupplierProfile;
}

const LEVEL_CLASS: Record<RiskLevel, string> = {
  low: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900',
  medium: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900',
  high: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900',
  critical: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900',
};

function LevelBadge({ level }: { level: RiskLevel }) {
  return <Badge variant="outline" className={LEVEL_CLASS[level]} data-testid="risk-level">{level.charAt(0).toUpperCase() + level.slice(1)}</Badge>;
}

function unwrap<T>(data: { results?: T[] } | T[]): T[] {
  return Array.isArray(data) ? data : (data.results ?? []);
}

/**
 * Risk tab on the supplier profile: the latest assessment (overall level,
 * category scores, next review), the assessment history, the 5×5 matrix view,
 * and — for customer users — a new assessment.
 */
export default function RiskTab({ supplier }: RiskTabProps) {
  const { user } = useAuth();
  const { portalMode } = useView();
  const readOnly = portalMode === 'supplier';
  const [assessments, setAssessments] = useState<RiskAssessment[]>([]);
  const [categories, setCategories] = useState<RiskCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([riskAPI.assessments(supplier.company), riskAPI.categories()])
      .then(async ([assessRes, catRes]) => {
        const list = unwrap<RiskAssessment>(assessRes.data);
        // The list serializer omits the category scores; fetch the latest in full.
        if (list.length > 0 && !list[0].category_risks) {
          const full = await riskAPI.assessment(list[0].id);
          list[0] = full.data;
        }
        setAssessments(list);
        setCategories(unwrap<RiskCategory>(catRes.data));
      })
      .catch((err: unknown) => {
        console.error('Risk load failed:', err);
        setError(getErrorMessage(err, 'Could not load risk assessments'));
      })
      .finally(() => setLoading(false));
  }, [supplier.company]);

  useEffect(() => { load(); }, [load]);

  const latest = assessments[0] ?? null;
  const categoryName = (id: number) => categories.find((c) => c.id === id)?.name ?? `Category ${id}`;
  const reviewDue = latest?.next_review_date ? latest.next_review_date <= new Date().toISOString().slice(0, 10) : false;

  if (error) {
    return (
      <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-sm font-medium">Could not load risk assessments</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="risk-tab">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">Risk assessment</h2>
          <p className="text-sm text-muted-foreground">Likelihood × consequence per category, rolled up to an overall level. Reassess at the review date or when something changes.</p>
        </div>
        <div className="flex gap-2">
          {latest && (
            <Button variant="outline" size="sm" onClick={() => setMatrixOpen(true)} data-testid="risk-view-matrix">
              <Grid3X3 className="mr-2 h-4 w-4" />View matrix
            </Button>
          )}
          {!readOnly && user?.company && (
            <Button size="sm" onClick={() => setDialogOpen(true)} disabled={categories.length === 0 && !loading} data-testid="risk-new">
              <ShieldAlert className="mr-2 h-4 w-4" />New assessment
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />)}
        </div>
      ) : !latest ? (
        <Card><CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="risk-empty">
          <div className="rounded-full bg-muted p-3"><ShieldAlert className="h-6 w-6 text-muted-foreground" /></div>
          <p className="text-sm font-medium">No risk assessment yet</p>
          <p className="max-w-md text-sm text-muted-foreground">
            {readOnly ? 'Your customer has not assessed this company\'s risk.'
              : categories.length === 0 ? 'No risk categories are set up on this platform — run the tenant setup to seed the standard set.'
                : 'Score the supplier on each category to get an overall risk level.'}
          </p>
        </CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card className={latest.overall_risk_level === 'high' || latest.overall_risk_level === 'critical' ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30' : undefined}>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Overall risk</p>
                <div className="mt-1 flex items-center gap-2">
                  <p className="text-2xl font-bold">{Number(latest.overall_risk_score).toFixed(0)}</p>
                  <LevelBadge level={latest.overall_risk_level} />
                </div>
                <p className="text-xs text-muted-foreground">Score out of 100</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Assessed</p>
                <p className="text-2xl font-bold">{formatDay(latest.assessment_date)}</p>
                <p className="text-xs text-muted-foreground">{latest.assessed_by_name || 'Assessor not recorded'}</p>
              </CardContent>
            </Card>
            <Card className={reviewDue ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950/30' : undefined}>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Next review</p>
                <p className="text-2xl font-bold">{formatDay(latest.next_review_date)}</p>
                <p className="text-xs text-muted-foreground">{reviewDue ? 'Review due' : 'On schedule'}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-lg">Category scores</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Likelihood</TableHead>
                    <TableHead className="text-right">Consequence</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(latest.category_risks ?? []).map((score) => (
                      <TableRow key={score.id} data-testid={`risk-score-${score.category}`}>
                        <TableCell className="font-medium">{score.category_name || categoryName(score.category)}</TableCell>
                        <TableCell className="text-right">{score.likelihood}</TableCell>
                        <TableCell className="text-right">{score.consequence}</TableCell>
                        <TableCell className="text-right font-semibold">{Number(score.risk_score).toFixed(0)}</TableCell>
                        <TableCell><LevelBadge level={score.risk_level} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{score.risk_description || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {(latest.summary || latest.recommendations) && (
            <Card>
              <CardContent className="space-y-3 pt-6 text-sm">
                {latest.summary && <div><p className="font-semibold text-foreground">Summary</p><p className="whitespace-pre-wrap text-muted-foreground">{latest.summary}</p></div>}
                {latest.recommendations && <div><p className="font-semibold text-foreground">Recommendations</p><p className="whitespace-pre-wrap text-muted-foreground">{latest.recommendations}</p></div>}
              </CardContent>
            </Card>
          )}

          {assessments.length > 1 && (
            <div>
              <h2 className="text-base font-semibold text-foreground">History <span className="font-normal text-muted-foreground">({assessments.length - 1} earlier)</span></h2>
              <div className="mt-2 space-y-2">
                {assessments.slice(1).map((a) => (
                  <div key={a.id} className="grid grid-cols-1 items-center gap-x-4 gap-y-1 rounded-md border p-3 text-sm sm:grid-cols-[11rem_minmax(0,1fr)_auto]">
                    <span>{formatDay(a.assessment_date)}</span>
                    <span className="text-muted-foreground">Score <span className="font-semibold text-foreground">{Number(a.overall_risk_score).toFixed(0)}</span> · {a.assessed_by_name || 'assessor not recorded'}</span>
                    <LevelBadge level={a.overall_risk_level} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <RiskMatrixModal open={matrixOpen} onClose={() => setMatrixOpen(false)} supplier={supplier} />
      {!readOnly && user?.company && (
        <RiskAssessmentDialog open={dialogOpen} onClose={() => setDialogOpen(false)} supplierId={supplier.company}
                              customerId={user.company} assessorId={user.id} categories={categories} onSaved={load} />
      )}
    </div>
  );
}
